import {
	ExclamationTriangleIcon,
	MagnifyingGlassIcon,
	PencilIcon,
	PlusIcon,
	TrashIcon,
	WrenchScrewdriverIcon,
	EyeIcon,
} from '@heroicons/react/24/outline';
import { Button, Spinner } from '../ui';
import type { Service } from '../../stores/servicesStore';

export interface ServiceListViewProps {
	services: Service[];
	isLoading: boolean;
	error: string | null;
	searchTerm: string;
	onSearchChange: (term: string) => void;
	/**
	 * Whether the viewer may create, edit, or delete. When false the Add button
	 * and the per-card Edit/Delete actions are not rendered at all — a
	 * view-only staff member sees the cards and nothing else.
	 */
	canManage: boolean;
	onView: (service: Service) => void;
	onAdd?: () => void;
	onEdit?: (service: Service) => void;
	onDelete?: (service: Service) => void;
	onRetry?: () => void;
}

/**
 * The search + card grid that both the Provider Settings → Services tab and the
 * Staff Services page render. Filtering lives here so the two can't drift.
 */
const ServiceListView = ({
	services,
	isLoading,
	error,
	searchTerm,
	onSearchChange,
	canManage,
	onView,
	onAdd,
	onEdit,
	onDelete,
	onRetry,
}: ServiceListViewProps) => {
	const term = searchTerm.trim().toLowerCase();
	const filteredServices = services.filter(
		service =>
			service.name.toLowerCase().includes(term) ||
			service.description.toLowerCase().includes(term)
	);

	return (
		<>
			{/* Mobile Add Button — the desktop one lives in the panel header */}
			{canManage && onAdd && (
				<Button variant="primary" onClick={onAdd} className="w-full mb-4 sm:hidden">
					<PlusIcon className="w-4 h-4 mr-1" />
					Add New Service
				</Button>
			)}

			{/* Search Bar */}
			{!isLoading && !error && services.length > 0 && (
				<div className="service-search">
					<MagnifyingGlassIcon className="service-search__icon" />
					<input
						type="text"
						className="service-search__input"
						placeholder="Search services..."
						value={searchTerm}
						onChange={e => onSearchChange(e.target.value)}
					/>
				</div>
			)}

			{isLoading ? (
				<div className="py-12 text-center">
					<Spinner
						size="lg"
						className="mx-auto mb-3 text-purple-600 dark:text-purple-400"
					/>
					<p className="text-sm text-gray-500 dark:text-gray-400">Loading services...</p>
				</div>
			) : error ? (
				<div className="py-12 text-center">
					<ExclamationTriangleIcon className="w-16 h-16 mx-auto text-red-300 dark:text-red-600 mb-4" />
					<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
						Failed to load services
					</h3>
					<p className="text-gray-500 dark:text-gray-400 mb-4">{error}</p>
					{onRetry && (
						<Button variant="secondary" onClick={onRetry}>
							Try Again
						</Button>
					)}
				</div>
			) : filteredServices.length === 0 ? (
				<div className="py-12 text-center">
					<WrenchScrewdriverIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
					<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
						{searchTerm ? 'No services found' : 'No Services Yet'}
					</h3>
					<p className="text-gray-500 dark:text-gray-400 mb-4">
						{searchTerm
							? 'Try a different search term'
							: canManage
								? 'Get started by adding your first service.'
								: 'No services have been set up for this location yet.'}
					</p>
					{!searchTerm && canManage && onAdd && (
						<Button variant="primary" onClick={onAdd}>
							<PlusIcon className="w-4 h-4 mr-1" />
							Add First Service
						</Button>
					)}
				</div>
			) : (
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
					{filteredServices.map(service => (
						<div key={service.id} className="service-card">
							<div className="service-card__body">
								<h3 className="service-card__name">{service.name}</h3>
								{service.description && (
									<p className="service-card__description">
										{service.description}
									</p>
								)}
								<div className="service-card__meta">
									{service.variants.length} combination
									{service.variants.length !== 1 ? 's' : ''}
								</div>
							</div>
							<div className="service-card__footer">
								<button
									onClick={() => onView(service)}
									className={`service-card__action ${
										canManage
											? 'service-card__action--view'
											: 'service-card__action--view-only'
									}`}
									title="View service"
								>
									<EyeIcon className="w-4 h-4" />
									{!canManage && <span>View Details</span>}
								</button>
								{canManage && onEdit && (
									<button
										onClick={() => onEdit(service)}
										className="service-card__action service-card__action--edit"
										title="Edit service"
									>
										<PencilIcon className="w-4 h-4" />
										<span>Edit</span>
									</button>
								)}
								{canManage && onDelete && (
									<button
										onClick={() => onDelete(service)}
										className="service-card__action service-card__action--delete"
										title="Delete service"
									>
										<TrashIcon className="w-4 h-4" />
									</button>
								)}
							</div>
						</div>
					))}
				</div>
			)}
		</>
	);
};

export default ServiceListView;
